import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    Output,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '../control-value-accessor-validator-base';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IContentLanguageLabelModel } from '../fi-content-language-shared/fi-content-language-shared';

@Component({
    selector: 'fi-answer-options-dnd-editor',
    templateUrl: './fi-answer-options-dnd-editor.component.html',
    styleUrls: ['./fi-answer-options-dnd-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-answer-options-dnd-editor', '[class.disabled]': 'isDisabled' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiAnswerOptionsDndEditor,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiAnswerOptionsDndEditor,
        },
    ],
    standalone: false,
})
export class FiAnswerOptionsDndEditor<
    T extends { Names: IContentLanguageLabelModel[] },
> extends ControlValueAccessorValidatorBase<T[]> {
    @Input() isMultiChoiceQuestion: boolean;
    @Input() isOptionNameRequiredFn: (optionName: IContentLanguageLabelModel, option: T) => boolean = (
        optionName: IContentLanguageLabelModel,
        option: T,
    ) => false;
    @Input({ required: true }) selectedLanguageId: number;
    @Input({ required: true }) getOptionInstanceFn: () => T;
    @Input({ required: true }) addOptionButtonText: string;
    @Input() deleteOptionButtonIconClass: string = 'BrandedColor';
    @Input() isDisabled: boolean = false;
    @Input() isDeleteOptionButtonDisabled: boolean = false;

    @Output() optionDeleted = new EventEmitter<void>();
    @Output() onDragDisableChange = new EventEmitter<boolean>();

    @HostBinding('class.new-layout') @Input() isNewLayout: boolean = false;
    @Input() optionTrailingActionsTemplate: TemplateRef<{ option: T; index: number }>;

    protected disableElementDrag(isDragDisable: boolean): void {
        this.onDragDisableChange.emit(isDragDisable);
    }

    protected onOptionDrop($event: CdkDragDrop<T[]>): void {
        if ($event.previousIndex === $event.currentIndex) {
            return;
        }
        moveItemInArray($event.container.data, $event.previousIndex, $event.currentIndex);
        this.disableElementDrag(false);
        this.doPropagateChanges();
    }

    protected deleteOption(presetOptionIndex: number): void {
        this.ngModel.splice(presetOptionIndex, 1);
        this.doPropagateChanges();
        this.optionDeleted.emit();
    }

    protected addOption(): void {
        if (this.ngModel.length === 0) {
            this.ngModel = [this.getOptionInstanceFn(), this.getOptionInstanceFn()];
        } else {
            this.ngModel.push(this.getOptionInstanceFn());
        }
        this.doPropagateChanges();
    }

    doValidate(control: AbstractControl): ValidationErrors | null {
        return {
            'missing-options': !this.ngModel?.length,
            'missing-option-names': this.ngModel?.some((option: T) => {
                return option.Names.find(
                    (name: IContentLanguageLabelModel) => this.isOptionNameRequiredFn(name, option) && !name.Value,
                );
            }),
        };
    }
}
